import React, { forwardRef } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import {
  getAppraisalData,
  selectAppraisal,
  selectImageUploadData
} from "../../../store/selectors";
import { formatImagesForPost, tryAgain } from "../helpers";
import { photoLabels } from "../../../data/photoLabels";
import FilePicker from "./FilePicker";
import { useDispatch } from "react-redux";
import { imageUploadSlice } from "../../../store/slices/ImageUploads";
import { removePhoto } from "../../../store/slices/Appraisal";
import { useLocation, useNavigate } from "react-router-dom";
import { handlePhotoUpload } from "../filestackRequestViaApi";
import { useDispatch as useAxiosDispatch } from "../context/axiosContext";

const createUploadHelper = (originalImage) => {
  return {
    id: originalImage.group,
    imageQuantityRequired: originalImage.qtyRequired || 1,
    helperText: originalImage.helperText,
    galleryHelperText: originalImage.galleryHelperText,
    tag: originalImage.tag || "Vehicle introduction"
  };
};
const ImageUploadDisplay = forwardRef(
  ({ retrying = false, image, multifilestackInProgress, showUploadMore }, ref) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { imageSrc, progress, imageIdentifier } = image;
    const { requiredPhotoData, mobileImageLastAction } = useSelector(selectImageUploadData);
    const {
      fileStack: { keys }
    } = useSelector(selectAppraisal);
    const { appraisalSessionId } = useSelector(getAppraisalData);
    const location = useLocation();
    const axiosDispatch = useAxiosDispatch();
    const galleryStyling = location.pathname === "/photo-gallery";
    const fileStackSrc = `https://cdn.filestackcontent.com/security=policy:${keys.policy},signature:${keys.signature}/${imageIdentifier}`;
    const isPhotoGallery = location.pathname === '/photo-gallery'

    const handleUploadNewImage = async (newUpload, previousImage) => {
      let foundUploadHelper = requiredPhotoData.find(
        (photo) =>
          photo.helperText === previousImage.helperText ||
          photo.id === previousImage.group
      );

      foundUploadHelper =
        foundUploadHelper ?? createUploadHelper(previousImage);

      const imageGroup = foundUploadHelper.id;

      await dispatch(
        imageUploadSlice.actions.setCurrentUploadHelper(foundUploadHelper)
      );

      if (newUpload) {
        if (!showUploadMore) {
          navigate(`/image-upload-progress/${imageGroup}`);
        }
        const newUploadsFormatted = formatImagesForPost(
          foundUploadHelper,
          newUpload
        );
        handlePhotoUpload(
          newUploadsFormatted,
          dispatch,
          foundUploadHelper,
          axiosDispatch,
          appraisalSessionId
        );
      }
      dispatch(imageUploadSlice.actions.setRetryingRequest(0));
    };

    const handleRemoveOldImage = async (image) => {
      await dispatch(imageUploadSlice.actions.resetFlagImageForDeletion());

      dispatch(
        imageUploadSlice.actions.removeCurrentImageIdentifier(
          image.imageIdentifier
        )
      );
      await dispatch(imageUploadSlice.actions.deleteImage(image));

      dispatch(
        removePhoto({
          imageIdentifier: image.imageIdentifier,
          appraisalSessionId
        })
      );
    };

    const handleImageChange = async (newImage) => {
      if (galleryStyling) {
        dispatch(imageUploadSlice.actions.setGalleryAccordionOpen());
      }
      await handleUploadNewImage(newImage, image);
      await handleRemoveOldImage(image);
    };

    return (
      <div
        className={`image-upload ch-ph--3 ch-pb--3 ch-pt--0 ch-rounded ch-bg--white ${
          !galleryStyling ? "ch-shadow--sm" : ""
        } ch-mv--2`}
        ref={ref}
      >
        <div className="ch-display--flex ch-justify-content--between ch-align-items--center ch-pt--2">
          {retrying ? (
            <>
              <p className="ch-fs--3 ch-mb--1 ch-color--ac-red ch-text--bold">
                Error
              </p>
              <p className="ch-fs--3 ch-mb--1">
                Retrying <span className="btn-loader retry-spinner" />
              </p>
            </>
          ) : (
            <>
              {!galleryStyling ? (
                <p className="ch-fs--3 ch-mb--1">
                  {progress === 100 ? "Complete!" : "Processing"} {progress}%
                </p>
              ) : (
                <h4 className="ch-width--12">{photoLabels[image.group]}</h4>
              )}
              <div className="ch-width--5 ch-mb--1">
                <FilePicker
                  data-testId="file-picker"
                  labelIdentifier={image.imageIdentifier}
                  buttonLabel="Retake"
                  buttonClass="ch-btn--link ch-color--ac-red ch-pr--0 ch-float--right"
                  disable={multifilestackInProgress || image.progress !== 100}
                  uploadMore
                  handleChange={handleImageChange}
                  mobileAction={isPhotoGallery ? null : mobileImageLastAction}
                />
              </div>
            </>
          )}
        </div>
        {!galleryStyling && (
          <div
            className={`loading-bar-container ch-bg--grey-3 ch-rounded ch-mb--1 ${
              retrying ? "connection-failed" : ""
            }`}
          >
            <span style={{ width: `${progress}%` }} className="ch-rounded " />
          </div>
        )}
        <img
          src={imageSrc ? imageSrc : fileStackSrc}
          alt=""
          onError={(e) => {
            tryAgain(e.target, fileStackSrc);
          }}
        />
      </div>
    );
  }
);

ImageUploadDisplay.propTypes = {
  retrying: PropTypes.bool,
  image: PropTypes.shape({
    progress: PropTypes.number,
    imageSrc: PropTypes.string,
    imageIdentifier: PropTypes.string
  })
};

export default ImageUploadDisplay;
